import React from 'react'
import { BaseView } from '@/components/base'
import { LinearGradient } from '@/components/LinearGradient'
import { useTheme } from '@/hooks/useTheme'
import { responsiveAdapter } from '@/utils'

interface CurrencyAvatarProps {
  uri?: string
  path?: string
  bgColor?: string | [string, string, ...string[]]
  size?: number
  iconSize?: number
  fallbackText?: string
  containerStyle?: React.CSSProperties
}

/**
 * CurrencyAvatar компонент - точная копия RN Avatar с isCurrency=true
 * Используется специально для отображения валют в CryptoSelector и других местах
 * где нужно соответствие RN версии
 */
export const CurrencyAvatar: React.FC<CurrencyAvatarProps> = ({
  uri,
  path,
  bgColor,
  size = 28,
  iconSize = 18,
  fallbackText,
  containerStyle,
}) => {
  const theme = useTheme()

  // Определяем источник изображения как в RN
  const imageSource = path || uri

  // Определяем цвета градиента как в RN Avatar
  const gradientColors = Array.isArray(bgColor)
    ? bgColor
    : [bgColor || '#ccc', bgColor || '#ccc']

  // Если у нас только один цвет, дублируем его для создания градиента
  const finalColors =
    gradientColors.length === 1
      ? [gradientColors[0], gradientColors[0]]
      : gradientColors

  return (
    <LinearGradient
      colors={finalColors}
      locations={finalColors.length === 3 ? [0, 0.15, 0.8] : undefined}
      start={{ x: 0.1, y: 0 }}
      end={{ x: 1, y: 1 }}
      circle
      circleSize={size}
      center
      style={containerStyle}
    >
      {imageSource ? (
        <img
          src={imageSource}
          alt={fallbackText || 'Currency'}
          style={{
            width: '100%', // Полная ширина как в RN CurrencyChildren
            height: `${responsiveAdapter(iconSize)}px`, // Используем iconSize как в RN
            objectFit: 'contain', // Как в RN: contentFit="contain"
          }}
          onError={(e) => {
            // Fallback на текст если изображение не загрузилось
            const target = e.target as HTMLImageElement
            target.style.display = 'none'
          }}
        />
      ) : (
        // Fallback на первую букву как в RN Avatar
        <div
          style={{
            fontSize: `${responsiveAdapter(40)}px`,
            fontFamily: 'semibold',
            color: theme.colors.label.buttonPrimaryEnabled,
            textAlign: 'center',
            fontWeight: '600',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {fallbackText?.charAt(0)?.toUpperCase() || '?'}
        </div>
      )}
    </LinearGradient>
  )
}
